import { createSelector } from '@reduxjs/toolkit';

import {
  selectAreRequiredApiCallsPending,
  selectIsAutoCompleteEnabled,
} from 'features/call/call-base/store/selectors';
import { isConferenceEmptySelector } from 'features/call/call-conference/store';
import { isDeafDisconnectedSelector } from 'features/call/call-deaf/store';
import { isAllHearingsDisconnectedSelector } from 'features/call/call-hearing/store';
import { selectIsFullyDisconnected } from 'features/caller-session/store';
import {
  selectIsEmergencyDeafReconnectionNeeded,
  selectIsSpawningEmergencyCall,
} from 'features/emergency/store';
import { selectIsSignMailSessionInProgress } from 'features/signmail/store';
import {
  isRemoteTeamingSelector,
  isTeamingStartedSelector,
} from 'features/teaming/teaming-base/store';

export const selectIsCallCanBeClosed = createSelector(
  isConferenceEmptySelector,
  isDeafDisconnectedSelector,
  isAllHearingsDisconnectedSelector,
  selectAreRequiredApiCallsPending,
  isRemoteTeamingSelector,
  isTeamingStartedSelector,
  selectIsEmergencyDeafReconnectionNeeded,
  selectIsSpawningEmergencyCall,
  selectIsFullyDisconnected,
  selectIsAutoCompleteEnabled,
  selectIsSignMailSessionInProgress,
  (
    isConferenceEmpty,
    isDeafDisconnected,
    isAllHearingsDisconnected,
    areRequiredApiCallsPending,
    isRemoteTeaming,
    isTeamingStarted,
    isEmergencyDeafReconnectionNeeded,
    isSpawningEmergencyCall,
    isCallerSessionCanEnd,
    isAutoCompleteEnabled,
    isSignMailSessionInProgress
  ) => {
    let reasonWhyCallDidNotAutoClose = '';

    if (isSignMailSessionInProgress) {
      reasonWhyCallDidNotAutoClose = 'sign mail session in progress';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }
    if (!isAutoCompleteEnabled) {
      reasonWhyCallDidNotAutoClose = 'auto complete disabled';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }
    if (!isCallerSessionCanEnd) {
      reasonWhyCallDidNotAutoClose = 'caller session is not fully disconnected';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }
    if (!isConferenceEmpty) {
      reasonWhyCallDidNotAutoClose = 'conference is not empty';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }
    if (areRequiredApiCallsPending) {
      reasonWhyCallDidNotAutoClose = 'connection state history in progress';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }
    if (isRemoteTeaming && isTeamingStarted) {
      reasonWhyCallDidNotAutoClose = 'remote teaming is started';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }
    if (isEmergencyDeafReconnectionNeeded) {
      reasonWhyCallDidNotAutoClose = 'emergency deaf reconnection needed';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }
    if (isSpawningEmergencyCall) {
      reasonWhyCallDidNotAutoClose = 'emergency call is spawning';
      return { canBeClosed: false, reasonWhyCallDidNotAutoClose };
    }

    const canBeClosed = isDeafDisconnected && isAllHearingsDisconnected;

    return {
      canBeClosed,
      reasonWhyCallDidNotAutoClose,
    };
  }
);
